import { sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import type { DesktopAnalyticsContext } from '@repo/analytics/amplitude/desktop-context'

type FontCategory = string
import { createEventTracker, createToolEvent } from '@repo/analytics/amplitude/event-tracker'
import type { BaseEventProps } from '@repo/analytics/amplitude/event-tracker'
import merge from 'lodash.merge'

/** Tool names as they appear in Amplitude. Every `<Tool> - <Event>` name is built from one of these. */
export const STUDIO_TOOLS = {
  adBannerResizer: 'Ad Banner Resizer',
  adCampaignGenerator: 'Ad Campaign Generator',
  aiCollageMaker: 'AI Collage Maker',
  aiColoringPageMaker: 'AI Coloring Page Maker',
  aiImageEditor: 'AI Image Editor',
  aiMockupGenerator: 'AI Mockup Generator',
  aiMusicGenerator: 'AI Music Generator',
  aiPhotoStyles: 'AI Photo Styles',
  aiQuoteGenerator: 'AI Quote Generator',
  aiVideoEditor: 'AI Video Editor',
  aiVideoGenerator: 'AI Video Generator',
  aiVideoUpscaler: 'AI Video Upscaler',
  assets: 'Assets',
  backgroundRemover: 'Background Remover',
  cameraAngle: 'Camera Angle',
  characterSheet: 'Character Sheet',
  designReviewer: 'Design Reviewer',
  doodlePetGenerator: 'Doodle Pet Generator',
  flow: 'Flow',
  imageAlive: 'Image Alive',
  imageDecomposition: 'Image Decomposition',
  imageInpainting: 'Image Inpainting',
  imageOutpainting: 'Image Outpainting',
  logoCreator: 'Logo Creator',
  promptBuilder: 'Prompt Builder',
  removePeopleFromBackground: 'Remove People From Background',
  studioEditor: 'Studio Editor',
  subtitleGenerator: 'Subtitle Generator',
  threeDModelGenerator: '3D Model Generator',
  threeDModelViewer: '3D Model Viewer',
  threeDPrintGenerator: '3D Print Generator',
  toonFamilyBuilder: 'ToonFamily Builder',
  upscaler: 'Upscaler',
  vectorizer: 'Vectorizer',
  videoEditingToolbox: 'Video Editing Toolbox',
  virtualTryOn: 'Virtual Try-on',
  watermarkRemover: 'Watermark Remover',
  weddingInviteGenerator: 'Wedding Invite Generator',
  workflows: 'Workflows',
  youtubeThumbnailGenerator: 'YouTube Thumbnail Generator',
} as const

export type StudioTool = (typeof STUDIO_TOOLS)[keyof typeof STUDIO_TOOLS]

interface WithFontId {
  fontId: string
  fontName?: string
}

interface WithGlyphs {
  glyphs: string[]
}

// Event type definitions
type CommonEvent =
  | 'Loaded Studio'
  | 'New Design Dialog Opened'
  | 'New Design Dialog Closed'
  | 'New Design Created'
type DesktopEvent =
  | 'Studio Desktop - Open'
  | 'Studio Desktop - First Open'
  | 'Studio Desktop - Local Model Installed'
  | 'Studio Desktop - LoRA Installed'
  | 'Studio Desktop - Release Notes Viewed'
  | 'Studio Desktop - Update Started From Notes'
type DesignerEvent =
  | 'Studio Project Created'
  | 'Studio Designer - Download'
  | 'Studio Designer - Download Dialog Opened'
  | 'Studio Designer - File Downloaded'
type FlowEvent = 'Flow - Flow Created' | 'Flow - Image Downloaded'

type FontEvent =
  | 'Font Generation Started'
  | 'Font Family Selected'
  | 'Font Visualized'
  | 'Font Discarded'
  | 'Font Saved'
  | 'Font Published'
  | 'Glyph Regenerated'
  | 'Font added to Studio'
  | 'Font Downloaded'
  | 'Font copied'
  | 'Font Prompt copied to clipboard'
  | 'Font Lightbox - overview viewed'
  | 'Font Lightbox - styles viewed'
  | 'Font Lightbox - glyphs viewed'

type ToolEvent<T extends string> =
  | `${T} - Uploaded`
  | `${T} - Discarded`
  | `${T} - Discarded All`
  | `${T} - Downloaded` // NB: this means 'user tried to click download', we are keeping the name for data continuity
  | `${T} - Download Succeeded`
  | `${T} - Added to Studio`
  | `${T} - Vectorized`
  | `${T} - Clicked Share`
  | `${T} - Upload Started`
  | `${T} - Upload Completed`
  | `${T} - Upload Failed`
  | `${T} - Started Session`
  | `${T} - Generated Iteration`

type FileConverterEvent =
  | 'File Converter - Files Selected'
  | 'File Converter - Files Uploaded'
  | 'File Converter - Files Converted'
  | 'File Converter - Converted Files Downloaded'
  | 'File Converter - Conversion Failed'
  | 'File Converter - Upload Failed'

type ContrastCheckerEvent =
  | 'Contrast Checker - Opened'
  | 'Contrast Checker - Colors Swapped'
  | 'Contrast Checker - Autofixed'
  | 'Contrast Checker - Color Picker Used'

type ColorConverterEvent =
  | 'Color Converter - Opened'
  | 'Color Converter - Color Entered'
  | 'Color Converter - Format Copied'

type MediaTippedEvent = 'Media Tipped'
type ReferralEvent = 'Referral Link Shared'
type PricingEvent = 'Pricing CTA Clicked'

export type ReferralShareMethod =
  | 'copy_link'
  | 'facebook'
  | 'messenger'
  | 'x'
  | 'whatsapp'
  | 'email'

interface ReferralLinkSharedEventProps extends BaseEventProps {
  share_method: ReferralShareMethod
  referral_code: string
}

export interface UploadFailedMetadata {
  mime_type?: string
  file_extension?: string
}

export type UploadFailedEventProps = {
  error_message?: string
} & UploadFailedMetadata

type StudioEvent =
  | CommonEvent
  | DesktopEvent
  | DesignerEvent
  | FlowEvent
  | FontEvent
  | ToolEvent<StudioTool>
  | 'AI Video Generator - Load New Items Shown'
  | 'AI Video Generator - Load New Items Clicked'
  | 'AI Collage Maker - Style Selected'
  | 'Ad Campaign Generator - Template Selected'
  | 'Ad Campaign Generator - Format Selected'
  | 'Ad Campaign Generator - Generated'
  | 'Ad Campaign Generator - Downloaded'
  | 'Ad Campaign Generator - Edit Clicked'
  | 'Ad Campaign Generator - Platform Switched'
  | 'Camera Angle - Deleted Session'
  | FileConverterEvent
  | ContrastCheckerEvent
  | ColorConverterEvent
  | MediaTippedEvent
  | ReferralEvent
  | PricingEvent

type BrandedStudioEvent = StudioEvent & { __brand: 'AmplitudeEvent' }

export function sendStudioEvent<T extends StudioEvent>(
  eventName: T,
  eventProperties?: Record<string, unknown>,
  callback?: () => void,
) {
  sendAmplitudeEvent(eventName as BrandedStudioEvent, eventProperties, callback)
}

// Common Events
export const trackLoadedStudio = createEventTracker<CommonEvent>('Loaded Studio')

// Desktop shell events. These carry the desktop context explicitly so the
// events are self-contained, even though the global Amplitude context also
// attaches it to every desktop-renderer event.
export const trackStudioDesktopOpen = (eventProperties: DesktopAnalyticsContext) => {
  sendStudioEvent('Studio Desktop - Open', { ...eventProperties })
}

/**
 * Fires once per persisted desktop profile. `callback` runs after the event is
 * actually sent (or drained from the offline queue), so callers can persist the
 * "already opened" flag only once the send succeeds.
 */
export const trackStudioDesktopFirstOpen = (
  eventProperties: DesktopAnalyticsContext,
  callback?: () => void,
) => {
  sendStudioEvent(
    'Studio Desktop - First Open',
    { ...eventProperties, desktop_first_open: true },
    callback,
  )
}

export interface StudioDesktopLocalModelInstalledEvent extends Record<string, unknown> {
  model_id: string
  model_version: string
  model_tool: string
  desktop_platform: DesktopAnalyticsContext['desktop_platform']
}

export type StudioDesktopLoraInstalledEvent =
  | {
      lora_source: 'catalog'
      lora_id: string
      desktop_platform: DesktopAnalyticsContext['desktop_platform']
    }
  | {
      lora_source: 'local' | 'trained'
      desktop_platform: DesktopAnalyticsContext['desktop_platform']
    }

export const trackStudioDesktopLocalModelInstalled = (
  eventProperties: StudioDesktopLocalModelInstalledEvent,
) => {
  sendStudioEvent('Studio Desktop - Local Model Installed', eventProperties)
}

export const trackStudioDesktopLoraInstalled = (
  eventProperties: StudioDesktopLoraInstalledEvent,
) => {
  sendStudioEvent('Studio Desktop - LoRA Installed', eventProperties)
}

export type ReleaseNotesSource = 'update_prompt' | 'whats_new_modal' | 'settings'

export interface StudioDesktopReleaseNotesViewedEvent extends Record<string, unknown> {
  version: string
  source: ReleaseNotesSource
  desktop_platform?: DesktopAnalyticsContext['desktop_platform']
}

export const trackStudioDesktopReleaseNotesViewed = (
  eventProperties: StudioDesktopReleaseNotesViewedEvent,
) => {
  sendStudioEvent('Studio Desktop - Release Notes Viewed', eventProperties)
}

export interface StudioDesktopUpdateStartedFromNotesEvent extends Record<string, unknown> {
  version: string
  source: ReleaseNotesSource
  desktop_platform?: DesktopAnalyticsContext['desktop_platform']
}

export const trackStudioDesktopUpdateStartedFromNotes = (
  eventProperties: StudioDesktopUpdateStartedFromNotesEvent,
) => {
  sendStudioEvent('Studio Desktop - Update Started From Notes', eventProperties)
}
export const trackNewDesignDialogOpened = createEventTracker<CommonEvent>(
  'New Design Dialog Opened',
)
export const trackNewDesignDialogClosed = createEventTracker<CommonEvent>(
  'New Design Dialog Closed',
)
export interface NewDesignCreatedEvent extends Record<string, unknown> {
  templateId: string | null
  width: number
  height: number
  orientation: string
  unit: string
  dpi: string
}

export const trackNewDesignCreated = (event: NewDesignCreatedEvent) => {
  sendStudioEvent('New Design Created', event)
}

export const trackReferralLinkShared = createEventTracker<
  ReferralEvent,
  ReferralLinkSharedEventProps
>('Referral Link Shared')

export const trackPricingCtaClicked = createEventTracker<
  PricingEvent,
  { isLoggedIn: boolean; variant: string }
>('Pricing CTA Clicked')

// Designer Events
export const trackStudioProjectCreation = createEventTracker<
  DesignerEvent,
  {
    user_ID?: string
    user_type: 'identified' | 'anon'
  }
>('Studio Project Created')
// Track when a file is downloaded from the designer
export const trackStudioDesignerImageDownloaded = createEventTracker<
  DesignerEvent,
  {
    fileType: string
    userId?: string
    projectId?: string
    minimizeFileSize?: boolean
    removeBackground?: boolean
    flipHorizontally?: boolean
  }
>('Studio Designer - Download')

// Track when the download dialog is opened
export const trackStudioDesignerDownloadDialogOpened = createEventTracker<
  DesignerEvent,
  {
    userId?: string
    projectId: string
  }
>('Studio Designer - Download Dialog Opened')

// Flow Events
export const trackFlowCreated = createEventTracker<FlowEvent>('Flow - Flow Created')

export const trackUnlimitedStylePresetSelected = createEventTracker<
  'unlimited_style_preset_selected',
  {
    styleId: string
    baseModel: string
    placement: 'models_grid' | 'featured_carousel'
    filter: string | null
  }
>('unlimited_style_preset_selected')
export const trackFlowImageDownloaded = createEventTracker<FlowEvent>('Flow - Image Downloaded')
export const trackFlowClickedShare = createToolEvent<'Flow', 'Clicked Share'>(
  'Flow',
  'Clicked Share',
)

// Editor Events

export const trackStudioEditorClickShare = createToolEvent<'Studio Editor', 'Clicked Share'>(
  'Studio Editor',
  'Clicked Share',
)

/// Font Events
interface FontEventProps extends BaseEventProps, WithFontId {
  fontFamilyType?: FontCategory
  fontGenerationSessionId?: string
}

const fontGenerationSessionId = crypto.randomUUID()

function getBaseFontGeneratorEventProperties(): { fontGenerationSessionId: string } {
  return { fontGenerationSessionId }
}

export const trackFontGenerationStarted = () => {
  sendStudioEvent('Font Generation Started', getBaseFontGeneratorEventProperties())
}

export const trackFontFamilySelected = (eventProperties: { fontFamilyType: string }) => {
  sendStudioEvent(
    'Font Family Selected',
    merge(eventProperties, getBaseFontGeneratorEventProperties()),
  )
}

export const trackFontVisualized = (eventProperties: FontEventProps & WithGlyphs) => {
  sendStudioEvent('Font Visualized', merge(eventProperties, getBaseFontGeneratorEventProperties()))
}

export const trackFontDiscarded = (eventProperties: FontEventProps & WithGlyphs) => {
  sendStudioEvent('Font Discarded', merge(eventProperties, getBaseFontGeneratorEventProperties()))
}

export const trackFontSaved = (eventProperties: {
  fontId: string
  glyphs: string[]
  fontDownloaded: boolean
  fontName: string
}) => {
  sendStudioEvent('Font Saved', merge(eventProperties, getBaseFontGeneratorEventProperties()))
}

export const trackFontPublished = (eventProperties: { fontId: string; glyphs: string[] }) => {
  sendStudioEvent('Font Published', merge(eventProperties, getBaseFontGeneratorEventProperties()))
}

export const trackGlyphRegenerated = (eventProperties: {
  glyphId: string
  fontId: string
  glyphRequested: string // Char
  glyphAccepted: boolean
  glyphCanceled: boolean
}) => {
  sendStudioEvent(
    'Glyph Regenerated',
    merge(eventProperties, getBaseFontGeneratorEventProperties()),
  )
}

export const trackFontAddedToStudio = (eventProperties: {
  fontId: string
  fontName: string | undefined
  userId: string | undefined
}) => {
  sendStudioEvent(
    'Font added to Studio',
    merge(eventProperties, getBaseFontGeneratorEventProperties()),
  )
}

export const trackFontDownloaded = (eventProperties: {
  fontId: string
  fontName: string | undefined
  userId: string | undefined
}) => {
  sendStudioEvent('Font Downloaded', merge(eventProperties, getBaseFontGeneratorEventProperties()))
}

export const trackFontCopied = (eventProperties: {
  fontId: string
  fontName: string | undefined
  userId: string | undefined
}) => {
  sendStudioEvent('Font copied', merge(eventProperties, getBaseFontGeneratorEventProperties()))
}

export const trackFontPromptCopiedToClipboard = (eventProperties: {
  fontId: string
  fontName: string | undefined
  userId: string | undefined
}) => {
  sendStudioEvent(
    'Font Prompt copied to clipboard',
    merge(eventProperties, getBaseFontGeneratorEventProperties()),
  )
}

export const trackFontLightboxOverviewViewed = (eventProperties: {
  fontId: string
  fontName: string | undefined
  userId: string | undefined
}) => {
  sendStudioEvent(
    'Font Lightbox - overview viewed',
    merge(eventProperties, getBaseFontGeneratorEventProperties()),
  )
}

export const trackFontLightboxStylesViewed = (eventProperties: {
  fontId: string
  fontName: string | undefined
  userId: string | undefined
}) => {
  sendStudioEvent(
    'Font Lightbox - styles viewed',
    merge(eventProperties, getBaseFontGeneratorEventProperties()),
  )
}

export const trackFontLightboxGlyphsViewed = (eventProperties: {
  fontId: string
  fontName: string | undefined
  userId: string | undefined
}) => {
  sendStudioEvent(
    'Font Lightbox - glyphs viewed',
    merge(eventProperties, getBaseFontGeneratorEventProperties()),
  )
}

/// File Converter Events
interface SelectedFile {
  file_size: number
  file_type: string // extension or mime type
}

export const trackFileConverterFilesSelected = createEventTracker<
  FileConverterEvent,
  {
    userId?: string
    total_size: number
    file_count: number
    selectedFiles: SelectedFile[]
    has_subscription: boolean
  }
>('File Converter - Files Selected')

export const trackFileConverterFilesUploaded = createEventTracker<
  FileConverterEvent,
  {
    userId?: string
    total_size: number
    file_count: number
    selectedFiles: SelectedFile[]
  }
>('File Converter - Files Uploaded')

export const trackFileConverterFilesConverted = createEventTracker<
  FileConverterEvent,
  {
    userId?: string
    target_formats: string[]
    source_formats: string[]
    total_size: number
    file_count: number
    selectedFiles: SelectedFile[]
    load_time_ms: number
  }
>('File Converter - Files Converted')

export const trackFileConverterConvertedFilesDownloaded = createEventTracker<
  FileConverterEvent,
  {
    userId?: string
    image_conversion_sessionid: string
    total_size?: number
    file_count: number
    download_type: 'zip_file' | 'single_file'
  }
>('File Converter - Converted Files Downloaded')

export const trackFileConverterConversionFailed = createEventTracker<
  FileConverterEvent,
  {
    userId?: string
    error_message?: string
    total_size?: number
    file_count?: number
    selectedFiles?: SelectedFile[]
    source_formats?: string[]
    target_formats?: string[]
  }
>('File Converter - Conversion Failed')

export const trackFileConverterUploadFailed = createEventTracker<
  FileConverterEvent,
  {
    userId?: string
    error_message?: string
    total_size?: number
    file_count?: number
    selectedFiles?: SelectedFile[]
    source_formats?: string[]
  }
>('File Converter - Upload Failed')
